import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";

// Create Axios instance
export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: Attach JWT token to /admin requests
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // If FormData is being sent, let the browser set Content-Type automatically
    // (it will include the multipart/form-data boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Attach token to admin routes (routes containing /admin/)
    // Admin token is required for all admin endpoints per API documentation
    if (config.url?.includes("/admin")) {
      const token = localStorage.getItem("auth_token");
      if (token) {
        // Format: Authorization: Bearer <TOKEN> (as per API documentation)
        config.headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor: Handle 401 and 403 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;

      // 401 Unauthorized: Logout and redirect to login
      if (status === 401) {
        // Clear auth data from localStorage
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        // Dispatch a storage event to notify AuthContext/Redux to clear state
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "auth_token",
            oldValue: localStorage.getItem("auth_token"),
            newValue: null,
          }),
        );
        // Redirect to login
        window.location.href = "/login";
      }

      // 403 Forbidden: Already handled by the component, but we can add logging
      if (status === 403) {
        // Let the component handle this
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
